import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import employeeAuthService from '../services/employee-auth.service';
import { useNavigate } from 'react-router-dom';
import './EmployeeCheckIn.css';

const EmployeeCheckIn = () => {
  const navigate = useNavigate();
  
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [checkInResponse, setCheckInResponse] = useState(null);
  const [todaysCheckIn, setTodaysCheckIn] = useState(null);
  const [officeLocation, setOfficeLocation] = useState(null);
  const [distance, setDistance] = useState(null);
  const [checkInHistory, setCheckInHistory] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [employeeInfo, setEmployeeInfo] = useState(null);

  // Check authentication and fetch check-in status on component mount
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      // Check if employee is logged in
      if (!employeeAuthService.isLoggedIn()) {
        navigate('/employee/login');
        return;
      }
      
      // Get employee info
      const empInfo = employeeAuthService.getUser();
      setEmployeeInfo(empInfo);
      
      // Fetch today's check-in status (if exists)
      fetchTodaysCheckInStatus();
      
      // Fetch office location
      fetchOfficeLocation();
      
      // Fetch check-in history
      fetchCheckInHistory();
      
      // Check location permission status
      checkLocationPermission();
    };

    checkAuthAndFetchData();
  }, [navigate]);

  // Handle API errors and token expiration
  const handleApiError = (err, defaultMessage) => {
    console.error('API error:', err);
    
    // Check if unauthorized (token expired or invalid)
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      // Redirect to login
      navigate('/employee/login');
      return;
    }
    
    // Display error message
    setError(err.response?.data?.message || err.response?.data?.error || defaultMessage);
    setLoading(false);
  };

  // Check location permission status
  const checkLocationPermission = () => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          setLocationPermission(result.state);
          result.onchange = () => {
            setLocationPermission(result.state);
          };
        });
    }
  };

  // Fetch today's check-in status
  const fetchTodaysCheckInStatus = async () => {
    try {
      const response = await apiService.getEmployeeCheckInToday();
      if (response.data && response.data.checkedIn) {
        setTodaysCheckIn(response.data.checkInData);
      }
    } catch (err) {
      // Silently fail - just means they haven't checked in
      console.log('No check-in for today yet');
    }
  };

  // Fetch office location
  const fetchOfficeLocation = async () => {
    try {
      const response = await apiService.getOfficeLocation();
      setOfficeLocation(response.data);
    } catch (err) {
      // Location might not be set in admin panel yet
      console.log('Office location not available');
    }
  };

  // Fetch check-in history
  const fetchCheckInHistory = async () => {
    try {
      const response = await apiService.getEmployeeCheckInHistory();
      setCheckInHistory(response.data.data || []);
    } catch (err) {
      console.log('Error fetching check-in history');
    }
  };

  // Calculate distance between two coordinates in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = 
      Math.sin(Δφ/2) * Math.sin(Δφ/2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Function to handle location verification
  const verifyLocation = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    setUserLocation(null);
    setDistance(null);
    setCheckInResponse(null);
  
    try {
      // Check if geolocation is supported by the browser
      if (!navigator.geolocation) {
        throw new Error('Konum hizmetleri tarayıcınız tarafından desteklenmiyor.');
      }
  
      // Detect iOS device
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
      // Get current position
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Store user location for map
            setUserLocation({ latitude, longitude });
            
            // Calculate distance if office location is available
            if (officeLocation) {
              const calculatedDistance = calculateDistance(
                latitude,
                longitude,
                officeLocation.latitude,
                officeLocation.longitude
              );
              setDistance(calculatedDistance);
            }
            
            // Update permission state since we successfully got location
            setLocationPermission('granted');
            
            // Send location to backend for verification
            const response = await apiService.submitEmployeeCheckIn({
              latitude,
              longitude
            });
  
            setSuccess(true);
            setCheckInResponse(response.data);
            setTodaysCheckIn(response.data.data);
            setShowMap(true);
            
            // Update check-in history
            fetchCheckInHistory();
            
            setLoading(false);
          } catch (err) {
            handleApiError(err, 'Check-in işlemi başarısız oldu.');
          }
        },
        (err) => {
          console.error('Geolocation error:', err);
          
          if (err.code === 1) { // PERMISSION_DENIED
            setLocationPermission('denied');
            if (isIOS) {
                setError(
                    'Safari\'de konum servislerine izin vermeniz gerekiyor. Talimatlar:\n' +
                    '1. Safari\'yi tamamen kapatın\n' +
                    '2. Ayarlar uygulamasını açın\n' +
                    '3. Gizlilik ve Güvenlik > Konum Servisleri\'ne dokunun\n' +
                    '4. Konum Servisleri\'nin AÇIK olduğundan emin olun\n' +
                    '5. Aşağı kaydırarak Safari\'yi bulun\n' +
                    '6. "Bu web sitesini kullanırken sor" seçeneğini seçin\n' +
                    '7. Safari\'yi yeniden açın ve giriş yapın'
                  );
            } else {
              setError('Konum izni reddedildi. Giriş yapabilmek için konumunuza erişim izni vermeniz gereklidir.');
            }
          } else if (err.code === 2) { // POSITION_UNAVAILABLE
            setError('Konum bilgileri kullanılamıyor. Lütfen telefonunuzdaki konum servislerinin açık olduğundan emin olun.');
          } else if (err.code === 3) { // TIMEOUT
            setError('Konum isteği zaman aşımına uğradı. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
          } else {
            setError('Konum alınırken bir hata oluştu. Lütfen tekrar deneyin.');
          }
          
          setLoading(false);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 0 
        }
      );
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  

  // Format date for display
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render check-in status
// Full renderCheckInStatus function
const renderCheckInStatus = () => {
    // Detect iOS device
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    return (
      <div className="employee-check-in-status">
        {todaysCheckIn ? (
          <div className="employee-checked-in">
            <div className="employee-status-header">
              <div className="employee-status-icon success">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3>Bugün İçin Giriş Yapıldı</h3>
            </div>
            
            <div className="employee-status-details">
              <div className="employee-status-item">
                <span className="employee-status-label">Giriş zamanı:</span>
                <span className="employee-status-value">
                  {formatDateTime(todaysCheckIn.checkInTime)}
                </span>
              </div>
              
              {todaysCheckIn.distance && (
                <div className="employee-status-item">
                  <span className="employee-status-label">Café'ye uzaklık:</span>
                  <span className="employee-status-value">
                    {Math.round(todaysCheckIn.distance)} metre
                  </span>
                </div>
              )}
            </div>
            
            <button
              onClick={verifyLocation}
              className="employee-verify-again-button"
            >
              Konumu Tekrar Doğrula
            </button>
          </div>
        ) : (
          <div className="employee-not-checked-in">
            <div className="employee-status-header">
              <div className="employee-status-icon warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h3>Henüz Giriş Yapılmadı</h3>
            </div>
            
            <p className="employee-location-prompt">
              Vardiya girişi yapmak için konumunuzu doğrulayın. Café bölgesinde olduğunuzdan emin olun.
            </p>
            
            {locationPermission === 'denied' ? (
              <div className="employee-permission-denied">
                <div className="employee-permission-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                  </svg>
                </div>
                
                <h4>Konum İzni Gerekli</h4>
                
                {isIOS ? (
                  <div className="ios-permission-instructions">
                    <p>iPhone'unuzda konum servislerine izin vermeniz gerekiyor. Lütfen şu adımları izleyin:</p>
                    
                    <ol className="ios-steps">
                      <li>
                        <strong>Safari'yi tamamen kapatın</strong> 
                        <small>(tüm sekmeleri kapatın ve ana ekrana dönün)</small>
                      </li>
                      <li>
                        <strong>Ayarlar</strong> uygulamasını açın
                      </li>
                      <li>
                        <strong>Gizlilik ve Güvenlik</strong> &gt; <strong>Konum Servisleri</strong>'ne dokunun
                      </li>
                      <li>
                        <strong>Konum Servisleri</strong>'nin AÇIK olduğundan emin olun
                      </li>
                      <li>
                        Aşağı kaydırın ve <strong>Safari Web Sitesi</strong>'ni bulun
                      </li>
                      <li>
                        <strong>"Bu web sitesini kullanırken sor"</strong> seçeneğini seçin
                      </li>
                      <li>
                        Safari'yi yeniden açın ve giriş yapın
                      </li>
                    </ol>
                    
                    <div className="ios-button-container">
                      <button onClick={() => window.location.reload()} className="employee-refresh-button">
                        <svg className="employee-button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 4v6h-6"></path>
                          <path d="M1 20v-6h6"></path>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                          <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
                        </svg>
                        Sayfayı Yenile
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="android-permission-instructions">
                    <p>Giriş yapabilmek için konum servislerine izin vermeniz gerekiyor.</p>
                    
                    <ol className="android-steps">
                      <li>
                        <strong>Tarayıcı adres çubuğunun yanındaki kilit/bilgi simgesine</strong> dokunun
                      </li>
                      <li>
                        <strong>Site ayarları</strong>'na dokunun
                      </li>
                      <li>
                        <strong>Konum</strong> izinlerini bulun
                      </li>
                      <li>
                        <strong>"İzin ver"</strong> seçeneğini seçin 
                      </li>
                      <li>
                        Sayfayı yenileyin ve tekrar giriş yapmayı deneyin
                      </li>
                    </ol>
                    
                    <div className="button-container">
                      <button 
                        onClick={verifyLocation} 
                        className="employee-retry-button"
                      >
                        Yeniden Dene
                      </button>
                      
                      <button 
                        onClick={() => window.location.reload()} 
                        className="employee-refresh-button"
                      >
                        <svg className="employee-button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 4v6h-6"></path>
                          <path d="M1 20v-6h6"></path>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                          <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
                        </svg>
                        Sayfayı Yenile
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={verifyLocation}
                disabled={loading}
                className="employee-verify-button"
              >
                {loading ? (
                  <>
                    <span className="employee-loading-spinner" />
                    Doğrulanıyor...
                  </>
                ) : (
                  <>
                    <svg className="employee-button-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    Konumumu Doğrula
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render the location map
  const renderLocationMap = () => {
    if (!userLocation || !officeLocation || !showMap) return null;
    
    // Calculate relative positions for the SVG map
    const mapSize = 300;
    const centerX = mapSize / 2;
    const centerY = mapSize / 2;
    
    // Scale the map based on the radius and distance
    const radius = officeLocation.radius || 300;
    const scaleFactor = mapSize / (radius * 3);
    const userDistance = distance * scaleFactor;
    const radiusSize = radius * scaleFactor;
    
    // Calculate the user position relative to office (center of svg)
    const latDiff = userLocation.latitude - officeLocation.latitude;
    const lngDiff = userLocation.longitude - officeLocation.longitude;
    const userX = centerX + (lngDiff * 111000 * scaleFactor); // rough conversion to meters
    const userY = centerY - (latDiff * 111000 * scaleFactor); // negative because SVG Y is inverted
    
    const isWithinRadius = distance <= radius;
    
    return (
      <div className="employee-location-map">
        <h3>Konum Bilgisi</h3>
        
        <div className="employee-map-container">
          <svg width={mapSize} height={mapSize} viewBox={`0 0 ${mapSize} ${mapSize}`}>
            {/* Office radius circle */}
            <circle 
              cx={centerX} 
              cy={centerY} 
              r={radiusSize} 
              fill="rgba(59, 130, 246, 0.1)" 
              stroke="rgba(59, 130, 246, 0.5)"
              strokeWidth="2"
            />
            
            {/* Office point */}
            <circle 
              cx={centerX} 
              cy={centerY} 
              r="6" 
              fill="#1d4ed8" 
            />
            <text 
              x={centerX} 
              y={centerY - 12} 
              textAnchor="middle" 
              fontSize="12"
              fill="#1d4ed8"
            >
              Ofis
            </text>
            
            {/* User point */}
            <circle 
              cx={userX} 
              cy={userY} 
              r="6" 
              fill={isWithinRadius ? "green" : "red"} 
            />
            <text 
              x={userX} 
              y={userY - 12} 
              textAnchor="middle" 
              fontSize="12"
              fill={isWithinRadius ? "green" : "red"}
            >
              Siz
            </text>
            
            {/* Line connecting office and user */}
            <line 
              x1={centerX} 
              y1={centerY} 
              x2={userX} 
              y2={userY} 
              stroke="gray" 
              strokeWidth="1" 
              strokeDasharray="4"
            />
          </svg>
          
          <div className="employee-distance-info">
            <p className="employee-distance-value">
              Ofise uzaklık: <strong>{Math.round(distance)}m</strong>
            </p>
            <p className={`employee-radius-status ${isWithinRadius ? 'success' : 'error'}`}>
              {isWithinRadius 
                ? `Giriş alanı içerisindesiniz (${radius}m)` 
                : `Giriş alanı dışındasınız (${radius}m)`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render check-in history
  const renderCheckInHistory = () => {
    if (!checkInHistory || checkInHistory.length === 0) return null;
    
    return (
      <div className="employee-check-in-history">
        <h3>Son Giriş Kayıtları</h3>
        
        <div className="employee-history-list">
          {checkInHistory.slice(0, 5).map((record, index) => (
            <div key={index} className="employee-history-item">
              <div className="employee-history-date">
                {formatDateTime(record.checkInTime)}
              </div>
              <div className="employee-history-status">
                {record.verificationStatus === 'verified' ? (
                  <span className="employee-status-badge success">Başarılı</span>
                ) : (
                  <span className="employee-status-badge error">Başarısız</span>
                )}
              </div>
              {record.distance && (
                <div className="employee-history-distance">
                  {Math.round(record.distance)}m
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render error/success messages
  const renderMessages = () => {
    return (
      <>
        {error && <div className="employee-error-message">{error}</div>}
        
        {success && checkInResponse && (
          <div className="employee-success-message">
            <svg className="employee-message-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <p>Giriş başarılı! Konumunuz doğrulandı.</p>
            <p className="employee-message-time">
              <svg className="employee-message-icon-small" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              {formatDateTime(checkInResponse.data.checkInTime)}
            </p>
          </div>
        )}
      </>
    );
  };

  // Main render function
  return (
    <div className="employee-check-in-component">
      <div className="employee-section-header">
        <h2>Vardiya Giriş</h2>
      </div>
      
      <div className="employee-content-layout">
        <div className="employee-check-in-main">
          {renderCheckInStatus()}
          {renderMessages()}
          {renderLocationMap()}
        </div>
        
        <div className="employee-check-in-sidebar">
          {renderCheckInHistory()}
        </div>
      </div>
    </div>
  );
};

export default EmployeeCheckIn;